/**
 * Main-thread client for `symbols.worker.ts`. Same role as `pipelineClient.ts`:
 * exposes `extractSymbolsInWorker` with the same shape as `extractSymbols` so
 * callers swap one import.
 *
 * Behavior:
 *  - One shared Worker instance for the page (outline extraction is naturally
 *    serialized — there's a single active document — and a pool would only
 *    multiply WASM memory for marginal gain).
 *  - Each call gets a monotonic id; results are routed back by id.
 *  - Lazy worker construction: the worker isn't spawned until the first call,
 *    so opening the app costs nothing if the user never opens a code outline.
 *  - In-thread fallback for environments without a usable Worker (SSR, test
 *    runners — jsdom Worker doesn't execute module workers). Falls back
 *    transparently: callers can't tell which path ran.
 */
import { extractSymbols } from '../symbols/treeSitter';
import type { CodeSymbol, SymbolLanguageId } from '@slate/core';
import type { SymbolsRequest, SymbolsResponse } from './symbols.worker';

/**
 * Worker is force-disabled under Vitest (`MODE === 'test'`) because jsdom's
 * Worker shim accepts `new Worker(...)` but never executes the module — calls
 * would hang. The in-thread fallback keeps the test suite working.
 */
const WORKER_ENABLED: boolean = (() => {
  try {
    const env = (import.meta as unknown as { env?: { MODE?: string } }).env;
    if (env?.MODE === 'test') return false;
  } catch {
    /* import.meta.env not available — fall through to runtime check */
  }
  return typeof Worker !== 'undefined';
})();

let worker: Worker | null = null;
let nextId = 0;
const pending = new Map<
  number,
  { resolve: (symbols: CodeSymbol[]) => void; reject: (err: Error) => void }
>();

function ensureWorker(): Worker | null {
  if (worker || !WORKER_ENABLED) return worker;
  try {
    const w = new Worker(new URL('./symbols.worker.ts', import.meta.url), {
      type: 'module',
      name: 'slate-symbols',
    });
    w.addEventListener('message', (e: MessageEvent<SymbolsResponse>) => {
      const slot = pending.get(e.data.id);
      if (!slot) return;
      pending.delete(e.data.id);
      if (e.data.ok) slot.resolve(e.data.symbols);
      else slot.reject(new Error(e.data.error));
    });
    w.addEventListener('error', (e) => {
      // Fatal worker error — fail every pending request and reset so the next
      // call recreates a fresh worker (or falls back in-thread).
      const err = new Error(e.message || 'symbols worker error');
      for (const slot of pending.values()) slot.reject(err);
      pending.clear();
      worker = null;
    });
    worker = w;
  } catch {
    worker = null;
  }
  return worker;
}

/**
 * Extract a code outline. Off main thread when a Worker is available;
 * in-thread fallback otherwise. Same return shape as `extractSymbols` so
 * callers swap one import.
 */
export function extractSymbolsInWorker(
  source: string,
  language: SymbolLanguageId,
): Promise<CodeSymbol[]> {
  const w = ensureWorker();
  if (!w) return extractSymbols(source, language);
  return new Promise<CodeSymbol[]>((resolve, reject) => {
    const id = ++nextId;
    pending.set(id, { resolve, reject });
    const req: SymbolsRequest = { id, source, language };
    w.postMessage(req);
  });
}

/** Whether `extractSymbolsInWorker` runs off the main thread (vs. fall back). */
export const workerEnabled = WORKER_ENABLED;

/** Test-only: tear down the shared worker and pending requests. */
export function _resetForTests(): void {
  for (const slot of pending.values()) slot.reject(new Error('reset'));
  pending.clear();
  if (worker) {
    worker.terminate();
    worker = null;
  }
  nextId = 0;
}
